import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios'; // Import AxiosError
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../Redux/store';
import { setAuth } from '../Redux/authSlice';

interface Branch {
  id: number;
  name: string;
}

interface ApiError {
  message?: string;
  // Add other fields if needed
}

const RegisterBranches: React.FC = () => {
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [userType, setUserType] = useState('admin'); // Default to 'admin'
  const [branch, setBranch] = useState<number | null>(null); // Store the branch ID
  const [branchName, setBranchName] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [formInvalid, setFormInvalid] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]); // State for branches
  const dispatch = useDispatch();
  const [userRole, setUserRole] = useState<string>('');
  const auth = useSelector((state: RootState) => state.auth.value);

  // Fetch branches from the API
  const fetchBranches = async () => {
    try {
      const response = await axios.get('https://api.sominnovations.xyz/Branches/branches/');
      if (response.data && Array.isArray(response.data.results)) {
        setBranches(response.data.results);
      } else {
        console.error('Invalid branches data format');
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  };

  useEffect(() => {
    fetchBranches(); // Fetch branches on component mount
  }, []);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!first_name || !last_name || !email || !password || !passwordConfirm) {
      setFormInvalid(true);
      return;
    }

    if (password !== passwordConfirm) {
      alert('Password and password confirmation do not match.');
      return;
    }

    // Prepare the payload
    const payload = {
      first_name,
      last_name,
      email,
      password,
      password_confirm: passwordConfirm,
      role: userType,
      branch, // Send the branch ID
      branch_name: branchName, // Send the branch name (if applicable)
    };

    console.log('Request Payload:', payload); // Log the payload

    try {
      const response = await axios.post('https://api.sominnovations.xyz/users/register', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Server Response:', response.data); // Log the response
      setRedirect(true);
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>; // Type assertion for AxiosError
      if (axiosError.response) {
        console.error('Server Error:', axiosError.response.data); // Log the server's error message
        alert(`Error: ${axiosError.response.data.message || 'Invalid request'}`);
      } else {
        console.error('Error:', axiosError.message); // Log generic errors
        alert('An error occurred while registering the user.');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };

    const fetchUserRole = async () => {
      try {
        const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
        setUserRole(data.user.role);
      } catch (error) {
        console.error('Failed to fetch user role:', error);
      }
    };

    fetchData();
    fetchUserRole();
  }, [dispatch]);

  const getCookie = (name: string) => {
    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  if (redirect) {
    return <Navigate to="/users" />;
  }

  return (
    <div className="container" style={{width:"800px", marginLeft:"200px"}}>
      {auth ? (
        <div className="row justify-content-center">
          <form onSubmit={submit}>
            <div className="mb-3">
              <label htmlFor="userType" className="form-label">
                User Type:
              </label>
              <select
                className="form-select"
                id="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
              >
                <option value="admin">Admin</option>
                <option value="accountant">Accountant</option>
              </select>
            </div>

            <div className="mb-3">
              <div className="form-floating">
                <input
                  type="text"
                  className="form-control"
                  id="firstName"
                  placeholder="First Name"
                  value={first_name}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <label htmlFor="firstName">First Name</label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-floating">
                <input
                  type="text"
                  className="form-control"
                  id="lastName"
                  placeholder="Last Name"
                  value={last_name}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
                <label htmlFor="lastName">Last Name</label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-floating">
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label htmlFor="email">Email address</label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-floating">
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <label htmlFor="password">Password</label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-floating">
                <input
                  type="password"
                  className="form-control"
                  id="passwordConfirm"
                  placeholder="Password Confirm"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                />
                <label htmlFor="passwordConfirm">Password Confirm</label>
              </div>
            </div>

            {/* Branch Dropdown */}
            <div className="mb-3">
              <div className="form-floating">
                <select
                  className="form-select"
                  id="branch"
                  value={branch || ''}
                  onChange={(e) => setBranch(Number(e.target.value))} // Set branch ID
                >
                  <option value="">Select a branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="branch">Branch</label>
              </div>
            </div>

            <button className="w-100 btn btn-lg btn-primary mt-3" type="submit">
              Submit
            </button>
          </form>
        </div>
      ) : (
        <div>You are not authorized to view this page.</div>
      )}
    </div>
  );
};

export default RegisterBranches;